import { Fragment, useMemo, useRef, useState } from "react";
import { Formik } from "formik";
import { Alert, Link, Stack, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useHomeschool } from "../hooks";
import { K12GradeLevels } from "../models";
import FormFields from "./FormFields";

export default function HSApplicationForm() {
  const { schema, uploadApplication } = useHomeschool();

  const [previousState, setPreviousState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [success, setSuccess] = useState(false);

  const formRef = useRef();

  const initialState = useMemo(() => {
    if (previousState != null) {
      return { ...previousState };
    }

    return {
      firstName: "",
      lastName: "",
      email: "",
      localSchoolDistrict: "",
      isDataCollectionAgreementAccepted: false,
      streetAddress: "",
      city: "",
      state: "",
      country: "USA",
      students: [
        {
          firstName: "",
          lastName: "",
          gradeLevel: K12GradeLevels.GK,
          email: "",
          letterOfIntent: "",
        },
      ],
    };
  }, [previousState]);

  async function onFormSubmit(formData) {
    setPreviousState(formData);
    setErrors([]);
    setLoading(true);
    const response = await uploadApplication(formData);
    setSuccess(response.success);
    if (!response.success) {
      setErrors(response.errors);
    }
    setLoading(false);
  }

  return success ? (
    <Alert severity="success" sx={{ my: 4 }}>
      <Typography>
        Thank you for applying for homeschool access! Your application was
        successfully submitted. You will receive an email confirmation at the
        Parent / Guardian email provided. Please allow up to 48 hours for us to
        review your application. If you have any questions, please reach out to
        us at <Link href="mailto:contact@sooth.fyi">contact@sooth.fyi.</Link>
      </Typography>
    </Alert>
  ) : (
    <Fragment>
      {errors.map((error, index) => (
        <Alert severity="error" key={`error-${index}`}>
          <Typography>{error}</Typography>
        </Alert>
      ))}
      <Formik
        initialValues={initialState}
        enableReinitialize={true}
        validationSchema={schema}
        validateOnChange={false}
        validateOnBlur={true}
        onSubmit={onFormSubmit}
        innerRef={formRef}
      >
        {({
          values,
          errors,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          touched,
          setFieldValue,
          setFieldTouched,
        }) => {
          return (
            <Stack
              component="form"
              onSubmit={handleSubmit}
              direction="column"
              sx={{ overflowY: "auto", my: 2, px: 1, py: 2 }}
            >
              <FormFields
                values={values}
                errors={errors}
                handleChange={handleChange}
                handleBlur={handleBlur}
                touched={touched}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
              />
              <Stack direction="row" justifyContent="end">
                <LoadingButton
                  loading={isSubmitting || loading}
                  variant="contained"
                  type="submit"
                >
                  Submit
                </LoadingButton>
              </Stack>
            </Stack>
          );
        }}
      </Formik>
    </Fragment>
  );
}
